<script setup lang="ts">
import { StatusSourceType } from "@/stores";
import { useFeaturesStore } from "@/stores/features";
import { Badge, type BadgeProps, Icon, Tooltip } from "@soft-db/ui";
import { useBatteryBadge } from "./composable/battery-badge";
import { useLiveDataBadge } from "./composable/live-data-badge";
import { useLiveStatus } from "./composable/live-status";
import { useStateBadge } from "./composable/state-badge";

const props = withDefaults(
	defineProps<{
		sourceType: StatusSourceType;
		sourceId: string;
		variant?: BadgeProps["variant"];
	}>(),
	{
		variant: "outline",
	}
);

const { t } = useI18n();

const status = useLiveStatus(
	() => props.sourceType,
	() => props.sourceId
);

const { hasFeature } = useFeaturesStore();

const stateBadge = useStateBadge(() => status.value?.state);
const batteryBadge = useBatteryBadge(status);

const liveDataBadge = useLiveDataBadge(status);
</script>

<template>
	<Badge
		v-if="stateBadge != null"
		class="h-8 min-w-28 flex-nowrap overflow-hidden whitespace-nowrap text-xs font-semibold"
		size="md"
		:color="stateBadge.color"
		:variant="variant"
	>
		<Icon size="sm">{{ stateBadge.icon }}</Icon>
		<span v-if="liveDataBadge != null" class="whitespace-nowrap">
			{{ liveDataBadge.value }}
		</span>
		<span v-else-if="stateBadge.state === 'offline'" class="whitespace-nowrap">
			{{ t("live-status.offline") }}
		</span>
		<Tooltip :tooltip="`${batteryBadge?.value}%`">
			<Icon v-if="hasFeature('integrity') && batteryBadge != null" small>
				{{ batteryBadge?.icon }}
			</Icon>
		</Tooltip>
	</Badge>
</template>
